import { format } from 'date-fns';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Flex } from 'rebass';
import styled from 'styled-components';

import Card from '@/components/Card';
import Dropdown from '@/components/Dropdown';
import CalendarIcon from '@/components/Icon/CalendarIcon';
import Text from '@/components/Text';
import useMutationCalendar from '@/hooks/mutations/useMutationCalendar';
import { formatTime } from '@/utils';
import dueValidation from '@/validation/DueValidation';

const NoDue = styled(Flex)`
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #ebebeb;
  border-radius: 50%;
  cursor: pointer;
  height: 2.7rem;
  width: 2.7rem;
  &:hover {
    background: #f7f7f7;
  }
`;

const IsDue = styled(Flex)`
  align-items: center;
  justify-content: center;
  background: #fff;
  border-radius: 1.35rem;
  cursor: pointer;
  height: 2.7rem;
  width: max-content;
  padding-left: 1rem;
  padding-right: 1rem;
  &:hover {
    background: #f7f7f7;
  }
`;

const DropdownMenu = styled(Card)`
  left: 0;
  margin-top: 10px !important;
  position: absolute;
  width: fit-content;
  z-index: 10;
`;

const Due = ({ id, title, type, appointment }) => {
  const [currentTask, setCurrentTask] = useState(null);
  const { mutateAsync } = useMutationCalendar();

  const color = useMemo(() => {
    if (!appointment || !appointment?.date_to || appointment.translated_date_to === 'Heute') return 'darkGrey';
    const dateInPastArrow = (firstDate, secondDate) =>
      new Date(firstDate).setHours(0, 0, 0, 0) <= new Date(secondDate).setHours(0, 0, 0, 0);
    if (dateInPastArrow(appointment.date_to, format(new Date(), 'yyyy-MM-dd'))) return 'primary';
    return 'darkGrey';
  }, [appointment]);

  const initialValues = {
    dueDate: appointment?.date_to ? format(new Date(appointment.date_to), 'dd.MM.yyyy') : '',
    time: appointment?.time_to || '',
    calendarId: appointment?.id || '',
  };

  const formikConfig = {
    initialValues,
    validationSchema: dueValidation(initialValues, 'Bitte überprüfen Sie Ihre Eingaben.'),
    enableReinitialize: true,
    onSubmit: async (value) => {
      await mutateAsync({ ...value, id, title, type, status: 0 });
    },
  };

  return (
    <Formik {...formikConfig}>
      <Dropdown isOpen={currentTask} onOpen={setCurrentTask}>
        <Dropdown.Icon onClick={() => setCurrentTask(id)}>
          {appointment?.date_to || appointment?.time_to ? (
            <IsDue>
              <Text variant="small" color={color}>
                {appointment?.translated_date_to}
                {appointment?.time_to && ' • '}
                {formatTime(appointment?.time_to)}
                {appointment?.time_to && ' Uhr'}
              </Text>
            </IsDue>
          ) : (
            <NoDue>
              <CalendarIcon />
            </NoDue>
          )}
        </Dropdown.Icon>

        {currentTask && (
          <DropdownMenu>
            <Form>
              <Dropdown.Form
                placeholder="TT.MM.JJJJ"
                type="birthdate"
                title="Fällig am"
                label="Fällig am"
                name="dueDate"
                minWidth="225px"
                flexBasis={['100%', '50%', 'unset']}
              />

              <Dropdown.Form
                placeholder="SS:MM"
                type="time"
                title="Fällig um"
                label="Fällig um"
                name="time"
                minWidth="225px"
                flexBasis={['100%', '50%', 'unset']}
              />
            </Form>
          </DropdownMenu>
        )}
      </Dropdown>
    </Formik>
  );
};

Due.propTypes = {
  appointment: PropTypes.shape({
    date_to: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    time_to: PropTypes.string,
    translated_date_to: PropTypes.string.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
};

export default Due;
